// Strip LED (redesign 2026-10, mockup "Strip LED e Alimentatori").
// Una scheda per famiglia (nome + IP + tensione) con la scelta di luce e bobina; filtri, prezzi,
// scheda tecnica e riga del carrello (codice, descrizione) sono gli stessi di prima.
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import '../css/CatalogoPage.css';
import '../css/ShopPage.css';
import successSound from '../assets/success.mp3';
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import {
  formatEuro, PdfButton, QtyStepper, AddButton, AddedToast, ChipGroup, MobileFiltersButton, FiltersSheet, SpecGrid, ProductPhoto,
} from './shopUi';

const LIGHTS = [
  { value: '2700K', swatch: '#FFC98A' },
  { value: '3000K', swatch: '#FFD8A0' },
  { value: '4000K', swatch: '#FFF1D6' },
  { value: 'RGB', swatch: 'linear-gradient(90deg,#ff4d4d,#ffd84d,#4dff88,#4db8ff,#b44dff)' },
  { value: 'RGB+W', swatch: 'linear-gradient(90deg,#ff4d4d,#4dff88,#4db8ff,#FFF1D6)' },
];
const lightSwatch = (value) => {
  const v = String(value || '');
  if (/RGB\+W/.test(v)) return LIGHTS[4].swatch;
  if (/RGB/.test(v)) return LIGHTS[3].swatch;
  if (/2700K\+4000K/.test(v)) return 'linear-gradient(90deg,#FFC98A,#FFF1D6)';
  if (/2700/.test(v)) return LIGHTS[0].swatch;
  if (/3000/.test(v)) return LIGHTS[1].swatch;
  if (/4000/.test(v)) return LIGHTS[2].swatch;
  return '#fff';
};
const uniq = (list) => [...new Set(list.filter(Boolean))];
const familyKey = (sl) => `${String(sl.name || '').trim()}|${sl.ipRating}|${sl.voltage}`;

const matchesStrip = (sl, f) =>
  (!f.ledColor || sl.ledColor === f.ledColor || String(sl.ledColor || '').includes(f.ledColor)) &&
  (!f.power || sl.power === f.power) &&
  (!f.ledsPerMeter || sl.ledsPerMeter === f.ledsPerMeter) &&
  (!f.ipRating || sl.ipRating === f.ipRating) &&
  (!f.voltage || sl.voltage === f.voltage);

const StripFamilyCard = ({ family, t, user, showPrices, calculateDiscountedPrice, handleDownload, handleAddToCart }) => {
  const [code, setCode] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSecond, setShowSecond] = useState(false);
  const items = family.items;
  const current = items.find((sl) => sl.code === code) || items[0];
  const isHD = /\bHD\b/i.test(current.name || '');
  const colors = uniq(items.map((sl) => sl.ledColor));
  const coils = uniq(items.filter((sl) => sl.ledColor === current.ledColor).map((sl) => sl.coilLength));
  const image = showSecond && current.image2 ? current.image2 : current.image;

  const pickColor = (color) => {
    const next = items.find((sl) => sl.ledColor === color && sl.coilLength === current.coilLength) ||
      items.find((sl) => sl.ledColor === color);
    if (next) setCode(next.code);
  };
  const pickCoil = (coil) => {
    const next = items.find((sl) => sl.ledColor === current.ledColor && sl.coilLength === coil);
    if (next) setCode(next.code);
  };

  return (
    <article className="sp-card">
      <div className="sp-photo">
        <ProductPhoto key={image} src={image ? urlAsset(image) : ''} alt={current.name} />
        <span className="sp-tags">
          {isHD && <span className="sp-tag is-hd">HD</span>}
          {current.ipRating && <span className={`sp-tag${current.ipRating === 'IP67' ? ' is-ip67' : ''}`}>{current.ipRating}</span>}
        </span>
        {current.datasheet && <PdfButton onClick={() => handleDownload(current.datasheet)} label={t('shop.datasheet')} />}
        {current.image2 && (
          <span className="sp-dots">
            <button type="button" aria-label="1" aria-pressed={!showSecond} onClick={() => setShowSecond(false)} />
            <button type="button" aria-label="2" aria-pressed={showSecond} onClick={() => setShowSecond(true)} />
          </span>
        )}
      </div>
      <div className="sp-body">
        <div className="sp-head">
          <strong>{String(current.name || '').trim()}</strong>
          {showPrices && (
            <span className="sp-net">
              {formatEuro(user ? calculateDiscountedPrice(current.price) : current.price)}
              {user && <small>{t('shop.net')}</small>}
            </span>
          )}
        </div>
        <SpecGrid
          cells={[
            { label: t('shop.specPower'), value: current.power },
            { label: t('shop.specVolt'), value: current.voltage },
            { label: 'LED/m', value: current.ledsPerMeter, highlight: isHD },
            { label: 'CRI', value: current.cri },
            { label: t('shop.specCoil'), value: current.coilLength },
            { label: t('shop.specCut'), value: current.cut },
            { label: 'PCB', value: current.pcbWidth },
            { label: 'IP', value: current.ipRating },
          ]}
        />
        <div className="sp-variants" role="group" aria-label={t('stripLed.ledColor')}>
          {colors.map((color) => (
            <button key={color} type="button" className="sp-vchip" aria-pressed={color === current.ledColor} onClick={() => pickColor(color)}>
              <i style={{ background: lightSwatch(color) }} />{color}
            </button>
          ))}
        </div>
        {coils.length > 1 && (
          <div className="sp-variants" role="group" aria-label={t('stripLed.coilLength')}>
            {coils.map((coil) => (
              <button key={coil} type="button" className="sp-vchip" aria-pressed={coil === current.coilLength} onClick={() => pickCoil(coil)}>
                {coil}
              </button>
            ))}
          </div>
        )}
        <div className="sp-buy">
          <span className="sp-code">
            <span>{t('shop.code')} <b>{current.code}</b></span>
            {showPrices && (
              <span>{t('shop.list')} {user ? <s>{formatEuro(current.price)}</s> : formatEuro(current.price)}</span>
            )}
          </span>
          <div className="sp-actions">
            <QtyStepper value={quantity} onChange={setQuantity} label={t('shop.quantity')} />
            <AddButton label={t('shop.add')} onClick={() => handleAddToCart(current, Math.max(1, parseInt(quantity, 10) || 1))} />
          </div>
        </div>
      </div>
    </article>
  );
};

const StripLed = ({ searchResults, showPrices }) => {
  const { addToCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stripLeds, setStripLeds] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    ledColor: '',
    power: '',
    ledsPerMeter: '',
    ipRating: '',
    voltage: ''
  });
  const [message, setMessage] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const messageTimer = useRef(null);

  // Valori potenza/densità dedotti dai prodotti reali (non hardcoded): evita che i
  // bottoni filtro finiscano fuori sincrono quando cambia il listino (es. nuove serie HD).
  const availablePowers = [...new Set(stripLeds.map(sl => sl.power).filter(Boolean))]
    .sort((a, b) => parseFloat(a) - parseFloat(b));
  const availableDensities = [...new Set(stripLeds.map(sl => sl.ledsPerMeter).filter(Boolean))]
    .sort((a, b) => parseFloat(a) - parseFloat(b));

  useEffect(() => {
    fetch('/api/products/config/stripled')
      .then(response => response.json())
      .then(data => {
        setStripLeds(data);
      })
      .catch(error => console.error('Error loading configuration file:', error));
  }, []);

  useEffect(() => () => clearTimeout(messageTimer.current), []);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // Base: risultati della ricerca se ci sono, altrimenti tutte le strip; poi i filtri
  const baseStrips = searchResults && searchResults.length > 0 ? searchResults : stripLeds;
  const families = useMemo(() => {
    const map = new Map();
    baseStrips.forEach((sl) => {
      const key = familyKey(sl);
      if (!map.has(key)) map.set(key, { key, items: [] });
      map.get(key).items.push(sl);
    });
    return [...map.values()];
  }, [baseStrips]);
  const visibleFamilies = families
    .map((fam) => ({ ...fam, items: fam.items.filter((sl) => matchesStrip(sl, activeFilters)) }))
    .filter((fam) => fam.items.length > 0);
  const visibleCodes = visibleFamilies.reduce((sum, fam) => sum + fam.items.length, 0);

  const handleFilterChange = (filterType, filterValue) => {
    let newFilters = { ...activeFilters };
    if (newFilters[filterType] === filterValue) {
      newFilters[filterType] = '';
    } else {
      newFilters[filterType] = filterValue;
    }
    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setActiveFilters({
      ledColor: '',
      power: '',
      ledsPerMeter: '',
      ipRating: '',
      voltage: ''
    });
  };

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play();
  };

  const handleAddToCart = (stripLed, quantity) => {
    const item = {
      code: stripLed.code,
      description: `
        ${stripLed.name},
        ${t('stripLed.power')}: ${stripLed.power},
        ${t('stripLed.ledColor')}: ${stripLed.ledColor},
        ${t('stripLed.ipRating')}: ${stripLed.ipRating},
        ${t('stripLed.voltage')}: ${stripLed.voltage},
        LED/m: ${stripLed.ledsPerMeter},
        ${t('stripLed.coilLength')}: ${stripLed.coilLength},
        ${t('stripLed.cut')}: ${stripLed.cut},
        CRI: ${stripLed.cri}
      `,
      unitPrice: stripLed.price,
      quantity: quantity,
      datasheet: stripLed.datasheet || null,   // ✅ qui stripLed, NON product

    };

    addToCart(item);
    playSound();
    showMessage(`${t('stripLed.addedToCart')} · ${String(stripLed.name || '').trim()} × ${quantity}`);
  };

  const showMessage = (text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(''), 2400);
  };

  const handleDownload = (datasheet) => {
    if (!datasheet) return;
    // urlAsset punta a goooled.it (dominio diverso da b2b.goooled.com): l'attributo
    // download di un <a> viene ignorato cross-origin, quindi apriamo in una nuova
    // scheda per non far navigare via il B2B dalla pagina corrente.
    window.open(urlAsset(datasheet), '_blank', 'noopener,noreferrer');
  };

  const calculateDiscountedPrice = (price) => {
    return user ? (price * (1 - user.discount / 100)).toFixed(2) : price.toFixed(2);
  };

  const hasFilters = Object.values(activeFilters).some(Boolean);
  const chipGroups = (
    <>
      <ChipGroup
        title={t('stripLed.ipRating')}
        options={[{ value: 'IP20', label: `IP20 ${t('shop.indoor')}` }, { value: 'IP67', label: `IP67 ${t('shop.outdoor')}` }]}
        active={activeFilters.ipRating}
        onToggle={(v) => handleFilterChange('ipRating', v)}
      />
      <ChipGroup
        title={t('stripLed.voltage')}
        options={[{ value: '24V', label: '24V' }, { value: '230V', label: '230V' }]}
        active={activeFilters.voltage}
        onToggle={(v) => handleFilterChange('voltage', v)}
      />
      <ChipGroup
        title={t('stripLed.power')}
        options={availablePowers.map((p) => ({ value: p, label: p }))}
        active={activeFilters.power}
        onToggle={(v) => handleFilterChange('power', v)}
      />
      <ChipGroup
        title="LED/m"
        options={availableDensities.map((d) => ({ value: d, label: d }))}
        active={activeFilters.ledsPerMeter}
        onToggle={(v) => handleFilterChange('ledsPerMeter', v)}
      />
    </>
  );
  const sheetCount = ['ipRating', 'voltage', 'power', 'ledsPerMeter'].filter((k) => activeFilters[k]).length;

  return (
    <div className="cat sp">
      <section className="cat-band">
        <div className="cat-band-in">
          <div className="cat-head">
            <div>
              <h1>{t('sidebar.stripLed')}</h1>
              <p>{t('shop.stripSubtitle')}</p>
            </div>
            <span className="cat-count">{t('shop.familiesCount', { f: visibleFamilies.length, n: visibleCodes })}</span>
          </div>

          <div className="cat-mounts is-5" role="group" aria-label={t('stripLed.ledColor')}>
            {LIGHTS.map((light) => {
              const count = families.filter((fam) => fam.items.some((sl) => matchesStrip(sl, { ...activeFilters, ledColor: light.value }))).length;
              return (
                <button
                  key={light.value}
                  type="button"
                  className="cat-mt"
                  aria-pressed={activeFilters.ledColor === light.value}
                  onClick={() => handleFilterChange('ledColor', light.value)}
                >
                  <span className="sp-light" style={{ background: light.swatch }} aria-hidden="true" />
                  <span>{light.value}</span>
                  <small>{count}</small>
                </button>
              );
            })}
          </div>

          <div className="cat-fbar">
            {chipGroups}
            <button type="button" className="cat-reset" onClick={resetFilters} disabled={!hasFilters}>
              {t('stripLed.resetFilters')}
            </button>
          </div>
          <MobileFiltersButton
            label={t('stripLed.filters')}
            count={sheetCount}
            onOpen={() => setSheetOpen(true)}
            onReset={resetFilters}
            resetLabel={t('stripLed.resetFilters')}
            hasFilters={hasFilters}
          />
        </div>
      </section>

      <section className="cat-grid-wrap">
        {visibleFamilies.length > 0 ? (
          <div className="sp-grid">
            {visibleFamilies.map((fam) => (
              <StripFamilyCard
                key={fam.key}
                family={fam}
                t={t}
                user={user}
                showPrices={showPrices}
                calculateDiscountedPrice={calculateDiscountedPrice}
                handleDownload={handleDownload}
                handleAddToCart={handleAddToCart}
              />
            ))}
          </div>
        ) : (
          <div className="cat-empty">
            <p>{t('shop.noResults')}</p>
            {hasFilters && <button type="button" className="cat-chip" onClick={resetFilters}>{t('stripLed.resetFilters')}</button>}
          </div>
        )}
      </section>

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('stripLed.filters')}
        resetLabel={t('stripLed.resetFilters')}
        onReset={resetFilters}
        hasFilters={hasFilters}
        showLabel={t('shop.showResults')}
      >
        {chipGroups}
      </FiltersSheet>

      <AddedToast message={message} onGoToCart={() => navigate('/cart')} goToCartLabel={t('shop.goToCart')} />
    </div>
  );
}

export default StripLed;
